import { useState, useEffect } from "react";
import type { Lead } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";
import type { StageConfigItem } from "./PipelineKanbanBoard";

type Props = {
  deals: Lead[];
  stageConfig?: StageConfigItem[];
};

const defaultStages: { stage: string; colorBar: string }[] = [
  { stage: "New Inquiry", colorBar: "bg-gray-400" },
  { stage: "Site Visit Scheduled", colorBar: "bg-blue-500" },
  { stage: "Site Visit Done", colorBar: "bg-indigo-500" },
  { stage: "Proposal Sent", colorBar: "bg-amber-500" },
  { stage: "Negotiation", colorBar: "bg-orange-500" },
  { stage: "Booking Done", colorBar: "bg-green-500" },
  { stage: "Lost", colorBar: "bg-red-400" },
];

export function PipelineForecastView({ deals = [], stageConfig }: Props) {
  const [stages, setStages] = useState<{ stage: string; colorBar: string }[]>(defaultStages);

  useEffect(() => {
    if (stageConfig && stageConfig.length > 0) {
      setStages(stageConfig.map((s) => ({ stage: s.stage, colorBar: s.colorBar || "bg-indigo-500" })));
      return;
    }

    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          setStages(
            items.map((s: any) => ({
              stage: s.name,
              colorBar: s.color || "bg-indigo-500",
            }))
          );
        }
      })
      .catch(() => {});
  }, [stageConfig]);

  const parseValueNumber = (valStr: string | undefined): number => {
    if (!valStr) return 0;
    const num = parseFloat(valStr.replace(/[₹,]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const formatValue = (val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${val.toLocaleString()}`;
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";
  };

  // ── 1. Calculate Pipeline by Stage ─────────────────────────
  const stageTotals = stages.map((st) => {
    const stageDeals = deals.filter((d) => (d.stage || "New Inquiry") === st.stage);
    const value = stageDeals.reduce((sum, d) => sum + parseValueNumber(d.value), 0);
    return {
      stage: st.stage,
      colorBar: st.colorBar,
      value,
      count: stageDeals.length,
    };
  });

  const maxStageValue = Math.max(...stageTotals.map((s) => s.value), 1);
  const totalPipelineVal = stageTotals.reduce((sum, s) => sum + s.value, 0);

  // ── 2. Calculate Top Agents dynamically ───────────────────
  const agentMap: Record<string, { agent: string; value: number; deals: number }> = {};

  deals.forEach((d) => {
    const agentName = d.assigned?.trim() || "Unassigned";
    const val = parseValueNumber(d.value);
    if (!agentMap[agentName]) {
      agentMap[agentName] = { agent: agentName, value: 0, deals: 0 };
    }
    agentMap[agentName].value += val;
    agentMap[agentName].deals += 1;
  });

  const agentDealTotals = Object.values(agentMap).sort((a, b) => b.value - a.value);
  const maxAgentValue = Math.max(...agentDealTotals.map((a) => a.value), 1);

  return (
    <div className="space-y-6">
      {/* Section 1: Pipeline Value by Stage */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Pipeline Distribution by Stage</h3>
            <p className="text-xs text-gray-500 mt-0.5">Live aggregated value and deal count per pipeline stage</p>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-500">Total Value</span>
            <p className="text-base font-bold text-indigo-600">{formatValue(totalPipelineVal)}</p>
          </div>
        </div>

        <div className="space-y-3.5 pt-2">
          {stageTotals.map((st) => {
            const pct = totalPipelineVal > 0 ? Math.round((st.value / totalPipelineVal) * 100) : 0;
            const barWidth = Math.max((st.value / maxStageValue) * 100, st.count > 0 ? 3 : 0);

            return (
              <div key={st.stage} className="flex items-center gap-3">
                <span className="w-[140px] text-xs font-semibold text-gray-700 flex-shrink-0 truncate" title={st.stage}>
                  {st.stage}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="h-7 bg-gray-100 rounded-lg overflow-hidden p-0.5">
                    <div
                      className={`h-full rounded-md ${st.colorBar} transition-all duration-500 flex items-center justify-end pr-2`}
                      style={{ width: `${barWidth}%` }}
                    >
                      {pct >= 10 && (
                        <span className="text-[10px] font-bold text-white leading-none">{pct}%</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-[130px] text-xs text-gray-700 flex-shrink-0 text-right whitespace-nowrap font-medium">
                  {formatValue(st.value)} <span className="text-gray-400 font-normal">({st.count})</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 2: Agent Performance / Pipeline Share */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Agent Performance &amp; Pipeline Share</h3>
            <p className="text-xs text-gray-500 mt-0.5">Real-time breakdown of pipeline value assigned across team members</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
            {agentDealTotals.length} Active {agentDealTotals.length === 1 ? "Agent" : "Agents"}
          </span>
        </div>

        {agentDealTotals.length > 0 ? (
          <div className="space-y-3.5 pt-2">
            {agentDealTotals.map((a) => {
              const pct = Math.max((a.value / maxAgentValue) * 100, 4);

              return (
                <div key={a.agent} className="flex items-center gap-3 relative">
                  <div className="flex items-center gap-2.5 relative z-10 flex-shrink-0 w-[140px]">
                    <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                      {getInitials(a.agent)}
                    </div>
                    <span className="text-xs font-semibold text-gray-800 truncate" title={a.agent}>
                      {a.agent}
                    </span>
                  </div>

                  <div className="flex-1 relative min-w-0">
                    <div className="h-7 bg-gray-50 rounded-lg overflow-hidden border border-gray-100 flex items-center">
                      <div
                        className="h-full bg-indigo-100/90 rounded-md transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                      <span className="absolute left-2.5 text-xs font-semibold text-gray-800 whitespace-nowrap">
                        {formatValue(a.value)}{" "}
                        <span className="text-gray-400 font-normal">
                          ({a.deals} deal{a.deals !== 1 ? "s" : ""})
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 text-sm">
            <i className="ri-user-unfollow-line text-2xl block mb-1" />
            No agent performance data found in current pipeline.
          </div>
        )}
      </div>
    </div>
  );
}